import type { EntitySignalStore, Mapper } from '@awork/core/state/signal-store/entitySignalStore'
import type { Injector, Signal } from '@angular/core'
import { computed } from '@angular/core'
import type { Observable } from 'rxjs'
import { areArraysEqual, filterAndMap, normalizeDateValue } from '@awork/core/state/signal-store/helpers'
import type { SelectOptions, SyncFilter } from '@awork/core/state/signal-store/types'
import { Order } from '@awork/core/state/signal-store/types'
import { deepEqual } from '@awork/_shared/functions/lodash'
import { toObservable } from '@angular/core/rxjs-interop'

/**
 * Query class for entity signal stores.
 * Provides functions to query the store and select entities.
 *
 * ### Usage example
 * ```ts
 * @Injectable({ providedIn: 'root' })
 * export class UserQuery extends EntitySignalQuery<User> {
 *   constructor(private userStore: UserStore) {
 *     super(userStore)
 *   }
 *
 *   get activeUser(): Signal<User> {
 *     return this.getActive()
 *   }
 * }
 * ```
 */
export class EntitySignalQuery<Entity extends { id: string }> {
  private entitySignalStore: EntitySignalStore<Entity>
  private readonly entityConstructor: Mapper<Entity>

  constructor(store: EntitySignalStore<Entity>) {
    this.entitySignalStore = store
    this.entityConstructor = store.getEntityConstructor()
  }

  /**
   * Maps an entity using the entity constructor
   * @param {Entity} entity
   * @returns {Entity | null}
   */
  mapEntity(entity: Entity): Entity | null {
    return entity ? this.entityConstructor(entity) : null
  }

  /**
   * Maps entities using the entity constructor
   * @param {Entity[]} entities
   * @returns {Entity[]}
   */
  mapEntities(entities: Entity[]): Entity[] {
    return entities.map(entity => this.mapEntity(entity))
  }

  /**
   * Gets the loading state
   * @returns {Signal<boolean>}
   */
  get queryLoading(): Signal<boolean> {
    return this.entitySignalStore.getIsLoading()
  }

  /**
   * Gets the error state
   * @returns {Signal<Error>}
   */
  queryError(): Signal<Error> {
    return this.entitySignalStore.getError()
  }

  /**
   * Gets the last sync date
   * @returns {Signal<Date | null>}
   */
  get queryLastSync(): Signal<Date | null> {
    return computed(() => {
      const lastSync = this.entitySignalStore.getLastSync()
      return lastSync() ? new Date(lastSync()) : null
    })
  }

  /**
   * Checks whether the store requires a full sync.
   * A sync cursor without a baseline is an unrecoverable state for delta sync because a delta only
   * returns changes since the cursor and can never restore the missing baseline.
   * @param {(entity: Entity) => boolean} isBaselineEntityFn Identifies entities that prove the baseline exists.
   * @returns {boolean} Whether a full fetch is required.
   */
  requiresFullSync(isBaselineEntityFn?: (entity: Entity) => boolean): boolean {
    const isLoading = this.entitySignalStore.getIsLoading()
    const lastSync = this.entitySignalStore.getLastSync()

    if (isLoading() || !lastSync()) {
      return true
    }

    const entities = this.entitySignalStore.entities()

    return isBaselineEntityFn ? !entities.some(isBaselineEntityFn) : !entities.length
  }

  /**
   * Checks whether a filtered sync requires a full fetch.
   * A filter without a sync cursor has no baseline. A filter that tracks entities missing from the
   * store has lost part of its baseline. A delta sync only returns changes since the cursor, so it
   * can never restore those entities.
   * @param {string} filterExpression
   * @returns {boolean} Whether a full fetch is required.
   */
  requiresFilteredFullSync(filterExpression: string): boolean {
    const syncFilter = this.querySyncFilters().find(entry => entry.filterExpression === filterExpression)

    if (!syncFilter?.lastSync) {
      return true
    }

    const entityMap = this.entitySignalStore.entityMap()

    return syncFilter.entityIds.some(entityId => !entityMap[entityId])
  }

  /**
   * Gets the loaded on date
   * @returns {Signal<Date | null>}
   */
  get queryLoadedOn(): Signal<Date | null> {
    return computed(() => {
      const loadedOn = this.entitySignalStore.getLoadedOn()
      return loadedOn() ? new Date(loadedOn()) : null
    })
  }

  /**
   * Gets the sync filters
   * @returns {Signal<SyncFilter[]>}
   */
  get querySyncFilters(): Signal<SyncFilter[]> {
    return this.entitySignalStore.getSyncFilters()
  }

  /**
   * Gets an entity by id
   * Optionally maps the entity using the entity constructor
   * @param {string} entityId
   * @returns {Entity | null}
   */
  private takeEntity(entityId: string): Entity | null {
    return this.entitySignalStore.entityMap()[entityId] || null
  }

  /**
   * Gets an entity by id
   * @param {string} entityId
   * @returns {Signal<Entity>}
   */
  queryEntity(entityId: string): Signal<Entity> {
    return computed(() => this.mapEntity(this.takeEntity(entityId)), { equal: deepEqual })
  }

  /**
   * Selects an entity by id
   * @param {string} entityId
   * @param {Injector} injector
   * @returns {Observable<Entity>}
   * @deprecated
   */
  selectEntity(entityId: string, injector: Injector): Observable<Entity> {
    return toObservable(this.queryEntity(entityId), { injector })
  }

  /**
   * Gets an entity by id
   * @param {string} entityId
   * @returns {Entity}
   */
  getEntity(entityId: string): Entity {
    return this.takeEntity(entityId)
  }

  /**
   * Gets entities by ids
   * @param {string[]} entityIds
   * @param {SelectOptions<Entity>} options
   * @returns {Entity[]}
   */
  private takeMany(entityIds: string[], options?: SelectOptions<Entity>): Entity[] {
    const entitiesMap = this.entitySignalStore.entityMap()
    const entities: Entity[] = []

    entityIds.forEach(entityId => {
      const entity = entitiesMap[entityId]

      if (entity) {
        entities.push(entity)
      }
    })

    return this.applySelectOptions(entities, options)
  }

  /**
   * Gets entities by ids
   * @param {string[]} entityIds
   * @param {SelectOptions<Entity>} options
   * @returns {Signal<Entity[]>}
   */
  queryMany(entityIds: string[], options?: SelectOptions<Entity>): Signal<Entity[]> {
    return computed(
      () => {
        return this.takeMany(entityIds, options)
      },
      { equal: areArraysEqual }
    )
  }

  /**
   * Applies filtering to entities based on filterBy options.
   * @param {Entity[]} entities The entities to filter
   * @param {SelectOptions<Entity>['filterBy']} filterBy The filter options
   * @returns {Entity[]} The filtered entities
   */
  private applyFiltering(entities: Entity[], filterBy: SelectOptions<Entity>['filterBy']): Entity[] {
    const entityConstructor = this.entitySignalStore.getStoreOptions().entityConstructor

    if (Array.isArray(filterBy)) {
      filterBy.forEach(filter => {
        entities = filterAndMap(entities, filter, entityConstructor)
      })
    } else {
      entities = filterAndMap(entities, filterBy, entityConstructor)
    }

    return entities
  }

  /**
   * Applies entity constructor mapping to entities.
   * @param {Entity[]} entities The entities to map
   * @returns {Entity[]} The mapped entities
   */
  private applyEntityMapping(entities: Entity[]): Entity[] {
    const entityConstructor = this.entitySignalStore.getStoreOptions().entityConstructor
    return entities.map(entityConstructor)
  }

  /**
   * Applies limit to entities based on limitTo option.
   * @param {Entity[]} entities The entities to limit
   * @param {number} limitTo The limit number
   * @returns {Entity[]} The limited entities
   */
  private applyLimit(entities: Entity[], limitTo: number): Entity[] {
    return entities.slice(0, limitTo)
  }

  /**
   * Applies filtering, sorting, and limiting to entities based on options.
   * @param {Entity[]} entities The entities to process
   * @param {SelectOptions<Entity>} options The processing options
   * @returns {Entity[]} The processed entities
   */
  private applySelectOptions(entities: Entity[], options?: SelectOptions<Entity>): Entity[] {
    if (options?.filterBy) {
      entities = this.applyFiltering(entities, options.filterBy)
    } else {
      entities = this.applyEntityMapping(entities)
    }

    if (options?.sortBy) {
      entities = this.sortEntities(entities, options)
    }

    if (options?.limitTo) {
      entities = this.applyLimit(entities, options.limitTo)
    }

    return entities
  }

  /**
   * Gets entities all entities.
   * Optionally filters, sorts and limits the result.
   * @param {SelectOptions<Entity>} options
   * @returns {Entity[]}
   */
  private takeAll(options?: SelectOptions<Entity>): Entity[] {
    const entities = this.entitySignalStore.entities()
    return this.applySelectOptions(entities, options)
  }

  /**
   * Gets all entities.
   * Optionally filters, sorts and limits the result.
   * @param {SelectOptions<Entity>} options
   * @returns {Signal<Entity[]>}
   */
  queryAll(options?: SelectOptions<Entity>): Signal<Entity[]> {
    return computed(
      () => {
        return this.takeAll(options)
      },
      { equal: areArraysEqual }
    )
  }

  /**
   * Selects all entities.
   * Optionally filters, sorts and limits the result.
   * @param {Injector} injector
   * @param {SelectOptions<Entity>} options
   * @returns {Observable<Entity[]>}
   * @deprecated
   */
  selectAll(injector: Injector, options?: SelectOptions<Entity>): Observable<Entity[]> {
    return toObservable(this.queryAll(options), { injector })
  }

  /**
   * Gets all entities
   * Optionally filters, sorts and limits the result.
   * @param {SelectOptions<Entity>} options
   * @returns {Entity[]}
   */
  getAll(options?: SelectOptions<Entity>): Entity[] {
    return this.takeAll(options)
  }

  /**
   * Gets the active entity
   * @returns {Signal<Entity>}
   */
  queryActive(): Signal<Entity> {
    return computed(
      () => {
        const activeId = this.entitySignalStore.getActive()
        return this.mapEntity(this.takeEntity(activeId()))
      },
      { equal: deepEqual }
    )
  }

  /**
   * Gets the active entity
   * @returns {Entity}
   */
  getActive(): Entity {
    const activeId = this.entitySignalStore.getActive()
    return this.takeEntity(activeId())
  }

  /**
   * Gets the count of entities
   * @param {(entity: Entity) => boolean} predicate
   * @returns {number}
   * @example
   *
   * takeCount()
   * takeCount(entity => entity.active)
   */
  private takeCount(predicate?: (entity: Entity) => boolean): number {
    if (predicate) {
      return this.entitySignalStore.entities().filter(predicate).length
    }

    return this.entitySignalStore.ids().length
  }

  /**
   * Gets the count of entities
   * @param {(entity: Entity) => boolean} predicate
   * @returns {Signal<number>}
   * @example
   *
   * queryCount()
   * queryCount(entity => entity.active)
   */
  queryCount(predicate?: (entity: Entity) => boolean): Signal<number> {
    return computed(() => {
      return this.takeCount(predicate)
    })
  }

  /**
   * Gets the latest server update timestamp, optionally limited to the given IDs.
   * @param {string[]} entityIds Optional entity IDs. An empty array selects no entities.
   * @returns {Signal<Date | null>}
   */
  queryLastUpdatedOn(entityIds?: string[]): Signal<Date | null> {
    return computed(() => this.takeLastUpdatedOn(entityIds))
  }

  /**
   * Reads stored entities by ID and finds their latest valid update timestamp.
   * @param {string[]} entityIds Optional entity IDs. Omit to check all stored entities.
   * @returns {Date | null}
   */
  private takeLastUpdatedOn(entityIds?: string[]): Date | null {
    const entities = entityIds ? this.takeMany(entityIds) : this.takeAll()
    let latestUpdatedOn: Date | null = null

    for (const entity of entities) {
      if (!('updatedOn' in entity)) {
        continue
      }

      const updatedOn = entity.updatedOn

      if (typeof updatedOn !== 'string' && !(updatedOn instanceof Date)) {
        continue
      }

      const updatedOnDate = normalizeDateValue(updatedOn)

      if (updatedOnDate && (!latestUpdatedOn || updatedOnDate.getTime() > latestUpdatedOn.getTime())) {
        latestUpdatedOn = updatedOnDate
      }
    }

    return latestUpdatedOn
  }

  /**
   * Gets the count of entities
   * @param {(entity: Entity) => boolean} predicate
   * @returns {number}
   * @example
   *
   * getCount()
   * getCount(entity => entity.active)
   */
  getCount(predicate?: (entity: Entity) => boolean): number {
    return this.takeCount(predicate)
  }

  /**
   * Gets the sync filter by filterExpression
   * @param {string} filterExpression
   * @returns {Signal<SyncFilter | null>}
   */
  querySyncFilter(filterExpression: string): Signal<SyncFilter | null> {
    return computed(() => {
      const syncFilters = this.querySyncFilters

      if (!syncFilters()?.length) {
        return null
      }

      const syncFilter = syncFilters().find(entry => entry.filterExpression === filterExpression)

      if (syncFilter) {
        return { ...syncFilter, lastSync: new Date(syncFilter.lastSync) }
      }

      return null
    })
  }

  /**
   * Query entities by filter expression
   * @param {string} filterExpression
   * @param {SelectOptions<Entity>} options
   * @returns {Signal<Entity[]>}
   */
  queryAllByFilterExpression(filterExpression: string, options?: SelectOptions<Entity>): Signal<Entity[]> {
    return computed(() => {
      const syncFilter = this.querySyncFilter(filterExpression)

      if (!syncFilter()) {
        return []
      }

      const entities = this.queryMany(syncFilter().entityIds, options)

      return entities()
    })
  }

  /**
   * Sorts entities based on the provided sort options
   * @param {Entity[]} entities
   * @param {SelectOptions<Entity>} options
   * @returns {Entity[]}
   */
  private sortEntities(entities: Entity[], options: SelectOptions<Entity>): Entity[] {
    if (!options?.sortBy) {
      return entities
    }

    return entities.sort((a, b) => {
      if (typeof options.sortBy === 'function') {
        return options.sortBy(a, b)
      } else {
        const order = options.sortByOrder === Order.DESC ? -1 : 1

        return a[options.sortBy] > b[options.sortBy] ? order : a[options.sortBy] < b[options.sortBy] ? -order : 0
      }
    })
  }
}
